import React, { useState } from 'react';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTransition } from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button';
import { Label } from '@atlaskit/form';
import Select, { RadioSelect, ValueType } from '@atlaskit/select';

import { Text, Flex, Stack, xcss } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import { InfoPanel } from '@css-xp/base';
import { SelectType } from '../../../billing-profile/src/types';
import { CartLinkedEntitlementsSchema, CartProductSchema } from '../../../../types';
import { getTimestampFromDate, SelectItem } from './match-bill-dates';
import { useStoreActions } from '../../../../store';

type BulkChangeBillDatesModalProps = {
  closeModal: () => void;
  checkedItemMap: Record<string, SelectType>;
  setOnSuccessDateChange: (
    value: {
      title?: string;
      description: string;
    } | null
  ) => void;
  linkedProducts?: Record<string, CartProductSchema>;
  linkedApps?: Record<string, CartLinkedEntitlementsSchema>;
};

const selectDateOptionStyles = xcss({
  paddingTop: 'space.150',
  paddingBottom: 'space.150',
});

export const billDateChangeDates = [
  { label: '1st of every month', value: '1' },
  { label: '15th of every month', value: '15' },
  { label: '28th of every month', value: '28' },
  { label: 'Custom day', value: 'custom' },
];

export const BulkChangeBillDatesModal: React.FC<BulkChangeBillDatesModalProps> = ({
  closeModal,
  checkedItemMap,
  setOnSuccessDateChange,
  linkedProducts = {},
  linkedApps = {},
}) => {
  const actions = useStoreActions();

  const submitMatchBillDates = () => {
    actions.setNextBillAnchorDate(checkedItemMap, getTimestampFromDate(selectedDate), linkedProducts);
    setOnSuccessDateChange({ title: 'New bill date added', description: 'It will be set when you confirm the order.' });
    closeModal();
  };
  const hasLinkedProductsAndApps = Object.keys(linkedApps).length > 0 || Object.keys(linkedProducts).length > 0;
  const [selectedDateOption, setSelectedDateOption] = useState<string | number | undefined>(undefined);
  const [selectedDate, setSelectedDate] = useState<ValueType<SelectItem, false>>();

  const customDateOptions = () => {
    const daysInMonthList = [];
    for (let i = 1; i <= 28; i++) {
      daysInMonthList.push({ label: i, value: i });
    }
    return daysInMonthList;
  };

  return (
    <ModalTransition>
      <Modal
        shouldScrollInViewport={true}
        onClose={closeModal}
        isBlanketHidden={false}
        width={'medium'}
        testId="match-bill-date-warning-modal"
      >
        <ModalHeader>
          <Heading size="medium">Change bill date</Heading>
        </ModalHeader>
        <ModalBody>
          <Flex direction="column" gap="space.200">
            {/* we need to show a warning info if selected products has linked products and apps */}
            {hasLinkedProductsAndApps && (
              <InfoPanel
                type="warning"
                title={'This action will affect other subscriptions'}
                message={
                  <Text>
                    Products and apps on the same site that belong to the same product family need to be billed
                    together, so they'll need to have the same new bill date. They'll be automatically included in your
                    order. <br />
                    <br /> Pro-rated adjustment will be applied to the next invoice.
                  </Text>
                }
              />
            )}
            <RadioSelect
              options={billDateChangeDates}
              placeholder="Select an option"
              testId="select-date-option"
              onChange={(option) => {
                setSelectedDateOption(option?.value);
                if (option?.value !== 'custom' && option?.value) {
                  const dateInNumber = Number(option.value);
                  setSelectedDate({ value: dateInNumber, label: dateInNumber });
                } else {
                  setSelectedDate(undefined);
                }
              }}
            />

            {/* should allow user to select custom day */}
            {selectedDateOption === 'custom' && (
              <Stack xcss={selectDateOptionStyles} testId="select-custom-date">
                <Label htmlFor="select-custom-day">Select the new billing cycle day</Label>
                <Select
                  inputId="select-custom-day"
                  onChange={(selectedOption) => {
                    setSelectedDate(selectedOption);
                  }}
                  options={customDateOptions()}
                  placeholder={'Select a number between 1 to 28'}
                  isSearchable
                  value={selectedDate}
                  blurInputOnSelect={true}
                />
              </Stack>
            )}

            {selectedDate?.value && (
              <InfoPanel
                type="information"
                message={<Text>This change will apply from the next billing cycle.</Text>}
              />
            )}
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button testId="modal-cancel-cta" appearance="subtle" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            testId="modal-confirm-cta"
            appearance="primary"
            isDisabled={selectedDate?.value === undefined}
            onClick={submitMatchBillDates}
            autoFocus
          >
            Confirm
          </Button>
        </ModalFooter>
      </Modal>
    </ModalTransition>
  );
};
